import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { CatalogWorkTypeData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Loader } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';

import type { IDataEditModal } from '../CatalogTypesWork';
import { CatalogWorkTypeCodeFilter, CatalogWorkTypeNameFilter } from '../catalogTypesWorkQuery';
import { CheckRowCell } from '../CheckRowCell';
import { RowMenu } from '../RowMenu';

interface ICatalogTypesWorkColumns {
    handleSetCheckedIds: Dispatch<SetStateAction<{ [key: number]: boolean }>>;
    checkedIds: { [key: number]: boolean };
    createOrEditModalHandler: (state: boolean) => void;
    setDataEditModal: Dispatch<SetStateAction<IDataEditModal | null>>;
    onRemoveSuccess: () => void;
    setFilterValues: Dispatch<SetStateAction<Record<string, string[]>>>;
    catalogTypesOfWorkRules: IUseAccess['catalogTypesOfWork'];
}

export const useCatalogTypesWorkColumns = ({
    checkedIds,
    handleSetCheckedIds,
    createOrEditModalHandler,
    setDataEditModal,
    onRemoveSuccess,
    setFilterValues,
    catalogTypesOfWorkRules,
}: ICatalogTypesWorkColumns) => {
    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                handleSetCheckedIds((ids: any) => ({ ...ids, [rowId]: checked }));
            },
        [handleSetCheckedIds],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue: any) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [setFilterValues],
    );

    const columns = useMemo<ColDef<CatalogWorkTypeData>[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 80,
                minWidth: 80,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: CatalogWorkTypeData }) =>
                    !data ? (
                        <Loader />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CheckRowCell
                                row={data}
                                catalogTypesOfWorkRules={catalogTypesOfWorkRules}
                                checkedIds={checkedIds}
                                onChange={handleChangeCheckbox(data.id)}
                            />
                            <RowMenu
                                createOrEditModalHandler={createOrEditModalHandler}
                                data={data}
                                setDataEditModal={setDataEditModal}
                                onRemoveSuccess={onRemoveSuccess}
                                catalogTypesOfWorkRules={catalogTypesOfWorkRules}
                            />
                        </div>
                    ),
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                sortable: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogWorkTypeCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: CatalogWorkTypeCodeFilter,
                },
            },
            {
                headerName: 'Наименование',
                field: 'name',
                minWidth: 150,
                flex: 1,
                sortable: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    changeFilterHandler,
                    dataAccessor: 'catalogWorkTypeNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: CatalogWorkTypeNameFilter,
                },
            },
            {
                headerName: 'Eд.изм.',
                field: 'unit',
                width: 120,
            },
        ],
        [
            catalogTypesOfWorkRules,
            changeFilterHandler,
            checkedIds,
            createOrEditModalHandler,
            handleChangeCheckbox,
            onRemoveSuccess,
            setDataEditModal,
        ],
    );

    return { columns };
};
